import { DateOnly } from '@if/core/date'
import { formatCurrency, formatCurrencyNoDecimals, formatNumber } from '@if/core/utils'
import { Caption, SectionTitle } from '@if/mui'
import { Box, Card, CardContent, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { LineChart } from '@mui/x-charts/LineChart'
import { AggregationPeriod, MerchantLoanActivityMetric, MetricDisplayMode } from '@src/types/MerchantStatistic'
import type { MouseEvent } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PERFORMANCE_TABS } from '../statistics-metrics'

interface PerformanceOverTimeChartProps {
  aggregatedMetrics: MerchantLoanActivityMetric[]
  displayMode: MetricDisplayMode
  aggregation: AggregationPeriod
  isLoading: boolean
}

const X_AXIS_DATE_FORMAT: Record<AggregationPeriod, Intl.DateTimeFormatOptions> = {
  [AggregationPeriod.Daily]: { month: 'short', day: 'numeric' },
  [AggregationPeriod.Weekly]: { month: 'short', day: 'numeric' },
  [AggregationPeriod.Monthly]: { month: 'short', year: 'numeric' },
}

// `@mui/x-charts` allots the y-axis a fixed 45px and ellipsizes any tick label wider than
// `width - tickSize - TICK_LABEL_GAP`, which truncates every currency tick. Sizing the axis to the
// widest label it will render avoids that.
const Y_AXIS_DEFAULT_WIDTH = 45
const Y_AXIS_LABEL_INSET = 8 // x-charts' tickSize (6) + TICK_LABEL_GAP (2)
// Upper bounds on Roboto's advance at the 12px tick font, `caption`'s 0.4px letterSpacing included.
const TICK_DIGIT_WIDTH = 7.1 // digits and '$' measure 7.07
const TICK_SEPARATOR_WIDTH = 3.6 // comma, space and nbsp measure 3.37 - 3.43

/** Every glyph is charged at or above its true advance, so this never falls short of what the axis
 *  will lay out — and it stays within ~1px of measuring the label in the DOM. */
const estimateLabelWidth = (label: string) =>
  [...label].reduce((width, char) => width + (/[0-9$]/.test(char) ? TICK_DIGIT_WIDTH : TICK_SEPARATOR_WIDTH), 0)

/** Upper bound on the topmost tick. The axis rounds its domain up to a nice value, which can carry
 *  the label into an extra digit (987 654 -> 1 000 000), and `nice()` can round past the next
 *  multiple of the leading digit (8.883 -> 10). The next power of ten is above every nice bound. */
const niceUpperBound = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return 0
  return 10 ** Math.ceil(Math.log10(value))
}

export const PerformanceOverTimeChart = ({
  aggregatedMetrics,
  displayMode,
  aggregation,
  isLoading,
}: PerformanceOverTimeChartProps) => {
  const { t, i18n } = useTranslation()
  const [activeTabKey, setActiveTabKey] = useState<(typeof PERFORMANCE_TABS)[number]['key']>('applications')

  const activeTab = PERFORMANCE_TABS.find((tab) => tab.key === activeTabKey) ?? PERFORMANCE_TABS[0]
  const metricKey = displayMode === MetricDisplayMode.Count ? activeTab.countKey : activeTab.amountKey

  const formatValue = (value: number | null) =>
    value == null ? '' : displayMode === MetricDisplayMode.Amount ? formatCurrency(value) : formatNumber(value)

  // Cents are noise on an axis, and an application is $300 minimum so ticks never need them. The
  // tooltip keeps full precision through the series' own `formatValue`.
  const formatTickValue = (value: number) =>
    displayMode === MetricDisplayMode.Amount ? formatCurrencyNoDecimals(value) : formatNumber(value)

  const highestValue = aggregatedMetrics.reduce((max, stat) => Math.max(max, stat[metricKey]), 0)
  const tickUpperBound = niceUpperBound(highestValue)
  const yAxisWidth = Math.max(
    Y_AXIS_DEFAULT_WIDTH,
    Math.ceil(estimateLabelWidth(formatTickValue(tickUpperBound))) + Y_AXIS_LABEL_INSET,
  )

  const handleTabChange = (_: MouseEvent<HTMLElement>, newTabKey: (typeof PERFORMANCE_TABS)[number]['key'] | null) => {
    if (newTabKey) setActiveTabKey(newTabKey)
  }

  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
          <SectionTitle>{t('merchantStatistics.performance.overTimeTitle')}</SectionTitle>
          <ToggleButtonGroup
            value={activeTabKey}
            exclusive
            onChange={handleTabChange}
            size="small"
            disabled={isLoading}
          >
            {PERFORMANCE_TABS.map((tab) => (
              <ToggleButton key={tab.key} value={tab.key}>
                {t(`merchantStatistics.performance.tabs.${tab.key}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        {isLoading ? (
          <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Caption>{t('common.loading')}</Caption>
          </Box>
        ) : (
          <LineChart
            xAxis={[
              {
                data: aggregatedMetrics.map((stat) => new DateOnly(stat.date)),
                valueFormatter: (timestamp: number) =>
                  new DateOnly(timestamp).toLocaleDateString(i18n.language, X_AXIS_DATE_FORMAT[aggregation]),
              },
            ]}
            yAxis={[{ valueFormatter: formatTickValue, width: yAxisWidth }]}
            series={[
              {
                data: aggregatedMetrics.map((stat) => stat[metricKey]),
                color: activeTab.color,
                valueFormatter: formatValue,
                showMark: false,
              },
            ]}
            height={400}
            grid={{ vertical: true, horizontal: true }}
          />
        )}
      </CardContent>
    </Card>
  )
}
